import React from 'react';
import changelogEntries from '../../changelog.json';

interface ChangelogEntry {
  version: string;
  date: string;
  sections: { [section: string]: string[] };
}

interface ChangelogBoardProps {
  maxEntries?: number;
}

const sectionColors: { [key: string]: string } = {
  Added: 'text-green-500',
  Fixed: 'text-blue-400',
  Changed: 'text-yellow-500',
  Enhanced: 'text-cyan-400',
  Security: 'text-red-500',
  Performance: 'text-indigo-400',
  'Technical Improvements': 'text-purple-400',
  'Developer Experience': 'text-orange-400',
  UI: 'text-pink-400',
  'UI/UX': 'text-pink-400',
  UX: 'text-pink-400',
};

const ChangelogBoard: React.FC<ChangelogBoardProps> = ({ maxEntries = 5 }) => {
  const allEntries: ChangelogEntry[] = changelogEntries as unknown as ChangelogEntry[];
  const entries = allEntries.slice(0, maxEntries);

  if (!entries.length) return null;

  // Duplicate entries for a seamless loop
  const duplicatedEntries = [...entries, ...entries];

  return (
    <div className="bg-white/10 dark:bg-gray-900/60 rounded-2xl p-6 mb-8 shadow-lg border border-white/10">
      <h2 className="text-2xl font-bold mb-4 text-blue-400 flex items-center gap-2">
        <span>Changelog</span>
        <span className="text-xs font-normal text-slate-400">(Latest updates)</span>
      </h2>
      <div className="h-72 overflow-hidden relative">
        <div className="animate-scroll absolute top-0 left-0 w-full">
          {duplicatedEntries.map((entry, index) => (
            <div key={`${entry.version}-${index}`} className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-lg font-semibold text-slate-200">v{entry.version}</span>
                <span className="text-xs text-slate-400">{entry.date}</span>
              </div>
              <div className="space-y-2 ml-2">
                {Object.entries(entry.sections).map(([section, items]) => (
                  <div key={section}>
                    <div className={`font-semibold ${sectionColors[section] || 'text-slate-300'} mb-1`}>{section}</div>
                    <ul className="list-disc ml-6 text-slate-200 text-sm">
                      {items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChangelogBoard;